import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { lang } from '../config/lang';
import { motion } from 'framer-motion';
import SafeImage from '../components/SafeImage';
import PageContentService from '../services/pageContentService';
import resolveImageUrl from '../utils/resolveImageUrl';
import { useInlineEdit } from '../components/admin/InlineEditContext';
import InlineEditableText from '../components/admin/InlineEditableText';
import InlineEditableImage from '../components/admin/InlineEditableImage';

const Home = () => {
  const { home } = lang.fr;
  const [page, setPage] = useState(null);
  const [hero, setHero] = useState({ title: '', subtitle: '', background_image: '' });
  const inlineCtx = useInlineEdit && useInlineEdit();

  useEffect(() => {
    (async () => {
      try {
        const p = await PageContentService.getPage('accueil');
        setPage(p);
        const sections = Array.isArray(p?.sections) ? p.sections : [];
        const heroSec = [...sections].reverse().find(s => (s.key === 'hero' || s.type === 'hero')) || null;
        const data = heroSec?.data || {};
        const currentLang = 'fr';
        const pickI18n = (obj, key) => {
          if (!obj) return '';
          const val = obj[key];
          if (val && typeof val === 'object') {
            return val[currentLang] || '';
          }
          return val || '';
        };
        const pageTitle = pickI18n(data, 'title') || hero.title;
        const pageSubtitle = pickI18n(data, 'subtitle') || hero.subtitle;
        const rawBg = data.background_image || '';
        const pageBg = resolveImageUrl(rawBg);

        setHero({
          title: pageTitle || '',
          subtitle: pageSubtitle || '',
          background_image: pageBg || ''
        });
      } catch (err) {
        console.error('Accueil: échec chargement PageContent', err);
        setPage(null);
        setHero({ title: '', subtitle: '', background_image: '' });
      }
    })();
  }, []);

  const projects = [];

  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900">
        {/* Image de fond dynamique si disponible */}
        {(() => {
          const bg = (inlineCtx && inlineCtx.getValue('hero', 'background_image', hero.background_image)) || hero.background_image;
          return bg ? (
          <div
            className="absolute inset-0 bg-cover"
            style={{ backgroundImage: `url(${resolveImageUrl(bg)})`, backgroundPosition: 'center 70%' }}
            aria-hidden="true"
          />
          ) : null;
        })()}
        {/* Bouton édition image */}
        {inlineCtx && (
          <div className="absolute top-4 right-4 z-30">
            <InlineEditableImage sectionKey="hero" fieldPath="background_image" />
          </div>
        )}
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 z-10"></div>
        <div className="relative z-20 text-center text-white px-4 max-w-6xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            {inlineCtx ? (
              <InlineEditableText
                value={inlineCtx.getValue('hero', 'title.fr', hero.title)}
                sectionKey="hero"
                fieldPath="title.fr"
                placeholder="Transformer je ne sais pas trop quoi"
              />
            ) : (
              hero.title || null
            )}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            {inlineCtx ? (
              <InlineEditableText
                value={inlineCtx.getValue('hero', 'subtitle.fr', hero.subtitle)}
                sectionKey="hero"
                fieldPath="subtitle.fr"
                placeholder="Experts en rénovation depuis plus de 10 ans, nous donnons vie à vos projets avec passion et savoir-faire"
              />
            ) : (
              hero.subtitle || null
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services" className="bg-primary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors">
              Découvrir nos services
            </Link>
            <Link to="/contact" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors">
              Demander un devis
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = sections.find(s => (s.key === 'expertise' || s.type === 'expertise')) || null;
              const t = sec?.data?.title?.fr || sec?.title || '';
              const d = sec?.data?.description?.fr || '';
              return (
                <>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('expertise', 'title.fr', t)}
                        sectionKey="expertise"
                        fieldPath="title.fr"
                        placeholder={"Nos Domaines d'Expertise"}
                      />
                    ) : (
                      t || null
                    )}
                  </h2>
                  <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('expertise', 'description.fr', d)}
                        sectionKey="expertise"
                        fieldPath="description.fr"
                        placeholder={"De la rénovation complète aux petits aménagements, nous maîtrisons tous les aspects de la transformation d'habitat"}
                      />
                    ) : (
                      d || null
                    )}
                  </p>
                </>
              );
            })()}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = sections.find(s => (s.key === 'expertise' || s.type === 'expertise'));
              const items = Array.isArray(sec?.data?.items) ? sec.data.items : [];
              if (!items.length) return null;
              return items.map((it, i) => (
                <div key={i} className="bg-gray-800 rounded-lg p-8 hover:bg-gray-700 transition-colors group">
                  <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-4 text-white">{it.title}</h3>
                  <p className="text-gray-300">{it.description}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Projets Section (afficher uniquement s'il y a des éléments) */}
      {(() => {
        const sections = Array.isArray(page?.sections) ? page.sections : [];
        const sec = sections.find(s => (s.key === 'gallery' || s.type === 'gallery')) || null;
        const items = Array.isArray(sec?.data?.images) ? sec.data.images : (Array.isArray(sec?.data?.items) ? sec.data.items : []);
        if (!items.length) return null;
        const t = sec?.data?.title?.fr || sec?.title || '';
        const d = sec?.data?.description?.fr || '';
        return (
          <section className="py-20 px-4 bg-gray-800">
            <div className="container mx-auto max-w-7xl">
              <div className="text-center mb-16">
                <>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('gallery', 'title.fr', t)}
                        sectionKey="gallery"
                        fieldPath="title.fr"
                        placeholder={"Nos Réalisations"}
                      />
                    ) : (
                      t || null
                    )}
                  </h2>
                  <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('gallery', 'description.fr', d)}
                        sectionKey="gallery"
                        fieldPath="description.fr"
                        placeholder={'Découvrez quelques-unes de nos plus belles réalisations qui témoignent de notre savoir-faire'}
                      />
                    ) : (
                      d || null
                    )}
                  </p>
                </>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((it, index) => {
                  const img = it.image || it.url || it.src || '';
                  const cat = it.category || it.label || 'Projet';
                  const title = it.title || it.name || `Projet ${index + 1}`;
                  const desc = it.description || it.subtitle || '';
                  return (
                    <div key={index} className="bg-gray-900 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-transform">
                      <div className="h-64 bg-gray-700 flex items-center justify-center overflow-hidden">
                        {img ? (
                          <SafeImage src={img} alt={title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">Aucune image</div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="inline-block bg-primary text-white text-xs px-3 py-1 rounded-full mb-3">
                          {cat}
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
                        {desc ? <p className="text-gray-300">{desc}</p> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Statistiques Section */}
      <section className="pt-14 pb-20 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Titre Nos Chiffres */}
          <div className="text-center mb-16">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = [...sections].reverse().find(s => (s.key === 'stats' || s.type === 'stats')) || null;
              const t = sec?.data?.title?.fr || sec?.title || 'Nos Chiffres';
              // Description avec fallbacks robustes + valeur par défaut si absente
              const d = (
                sec?.data?.description?.fr ??
                sec?.data?.description ??
                sec?.description?.fr ??
                sec?.description ??
                sec?.data?.subtitle?.fr ??
                sec?.data?.subtitle ??
                ''
              );
              return (
                <>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('stats', 'title.fr', t)}
                        sectionKey="stats"
                        fieldPath="title.fr"
                        placeholder={'Nos Chiffres'}
                      />
                    ) : (
                      t || null
                    )}
                  </h2>
                  <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-6">
                    {inlineCtx ? (
                    <InlineEditableText
                        value={inlineCtx.getValue('stats', 'description.fr', d)}
                        sectionKey="stats"
                        fieldPath="description.fr"
                        placeholder={'Un aperçu clair de nos résultats et de notre impact'}
                      />
                    ) : (
                      d || null
                    )}
                  </p>
                </>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = [...sections].reverse().find(s => (s.key === 'stats' || s.type === 'stats')) || null;
              const items = Array.isArray(sec?.data?.items) ? sec.data.items : [];
              if (!items.length) return null;
              return items.map((it, idx) => (
                <div key={idx}>
                  <div className="text-4xl md:text-6xl font-bold text-primary mb-2">{it.number || it.nombre_elements || it.value || ''}</div>
                  <div className="text-gray-300 text-lg">{it.label_fr || it.label || ''}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Témoignages Section */}
      <section className="py-20 px-4 bg-gray-800">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            {(() => {
              const sections = Array.isArray(page?.sections) ? page.sections : [];
              const sec = sections.find(s => (s.key === 'testimonials' || s.type === 'testimonials')) || null;
              const t = sec?.data?.title?.fr || sec?.title || '';
              const d = sec?.data?.description?.fr || '';
              return (
                <>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('testimonials', 'title.fr', t)}
                        sectionKey="testimonials"
                        fieldPath="title.fr"
                        placeholder={"Ce que disent nos clients"}
                      />
                    ) : (
                      t || null
                    )}
                  </h2>
                  <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                    {inlineCtx ? (
                      <InlineEditableText
                        value={inlineCtx.getValue('testimonials', 'description.fr', d)}
                        sectionKey="testimonials"
                        fieldPath="description.fr"
                        placeholder={'La satisfaction de nos clients est notre plus grande récompense'}
                      />
                    ) : (
                      d || null
                    )}
                  </p>
                </>
              );
            })()}
          </div>
          
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-6 snap-x snap-mandatory justify-center">
              {(() => {
                const sections = Array.isArray(page?.sections) ? page.sections : [];
                const sec = sections.find(s => (s.key === 'testimonials' || s.type === 'testimonials')) || null;
                const items = Array.isArray(sec?.data?.items) ? sec.data.items : [];
                if (!items.length) return null;
                return items.map((testimonial, index) => (
                <div key={index} className="bg-gray-900 rounded-lg p-8 flex-none min-w-[320px] snap-start text-center">
                  <div className="flex items-center justify-center mb-4">
                    {[...Array(testimonial.rating || testimonial.note || 5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 text-lg italic">"{testimonial.comment || testimonial.avis || ''}"</p>
                  <div>
                    <div className="text-white font-semibold">{testimonial.name || testimonial.nom || 'Client'}</div>
                    <div className="text-primary text-sm">{testimonial.project || testimonial.projet || ''}</div>
                  </div>
                </div>
              ));
            })()}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          {(() => {
            const sections = Array.isArray(page?.sections) ? page.sections : [];
            const sec = sections.find(s => (s.key === 'cta' || s.type === 'cta')) || null;
            const t = sec?.data?.title?.fr || sec?.title || '';
            const d = sec?.data?.description?.fr || '';
            return (
              <>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {inlineCtx ? (
                    <InlineEditableText
                      value={inlineCtx.getValue('cta', 'title.fr', t)}
                      sectionKey="cta"
                      fieldPath="title.fr"
                      placeholder={"Prêt à transformer notre espace ?"}
                    />
                  ) : (
                    t || null
                  )}
                </h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  {inlineCtx ? (
                    <InlineEditableText
                      value={inlineCtx.getValue('cta', 'description.fr', d)}
                      sectionKey="cta"
                      fieldPath="description.fr"
                      placeholder={"Contactez-nous dès aujourd'hui pour une consultation gratuite et obtenez un devis personnalisé pour votre projet."}
                    />
                  ) : (
                    d || null
                  )}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact" className="bg-primary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary/90 transition-colors">
                    Demander un devis gratuit
                  </Link>
                  <Link to="/about" className="bg-transparent border-2 border-primary text-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary hover:text-white transition-colors">
                    En savoir plus
                  </Link>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">2RENOV</h3>
              <p className="text-gray-400 mb-4">
                Votre partenaire de confiance pour tous vos projets de rénovation depuis plus de 10 ans.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.878-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.758-1.378l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.624 0 11.99-5.367 11.99-11.99C24.007 5.367 18.641.001 12.017.001z" />
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/services" className="hover:text-primary transition-colors">Rénovation Intérieure</Link></li>
                <li><Link to="/services" className="hover:text-primary transition-colors">Rénovation Extérieure</Link></li>
                <li><Link to="/professional" className="hover:text-primary transition-colors">Professionnel</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Devis Gratuit</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Entreprise</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-primary transition-colors">À Propos</Link></li>
                <li><Link to="/contact" className="hover:text-primary transition-colors">Contact</Link></li>
                <li><a href="#" className="hover:text-primary transition-colors">Mentions Légales</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">CGV</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>123 Rue de la Rénovation</li>
                <li>75000 Paris, France</li>
                <li>+33 1 23 45 67 89</li>
                <li>contact@2renov.fr</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 2RENOV. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      
    </div>
  );
};

export default Home;
